import React, { useState, useEffect } from 'react';
import { useMandantStore } from '../../store/mandantStore';
import { loadFaelligkeiten, type VguFaelligkeit } from '../../services/vorsorgeService';
import { loadRechnungen } from '../../services/rechnungenService';
import { loadSetting } from '../../services/settingsService';
import { TATBESTAND_MAP } from '../vorsorge/data/tatbestaende';
import type { Rechnung } from '../abrechnung/types';

interface GefaehrdungsEintrag {
  id:          string;
  gefaehrdung: string;
  eintrittsWs: number;   // 1–5, entspricht dem ASA-Modul-Feld
  schwere:     number;   // 1–5
  massnahme:   string;
  erledigt:    boolean;
  frist:       string;
}

// ── Live-Daten aggregieren ────────────────────────────────────────────────

interface VguStat {
  tatbestandCode: string;
  bezeichnung:    string;
  faelligThisYear: number;
  durchgefuehrt:  number;
  offen:          number;
}

interface RechnungsStat {
  offen:    number;
  bezahlt:  number;
  gemahnt:  number;
  umsatz:   number;   // in Cent
}

function aggregateVgu(faelligkeiten: VguFaelligkeit[]): VguStat[] {
  const map = new Map<string, VguStat>();
  for (const f of faelligkeiten) {
    if (!map.has(f.tatbestandCode)) {
      map.set(f.tatbestandCode, {
        tatbestandCode: f.tatbestandCode,
        bezeichnung:    TATBESTAND_MAP.get(f.tatbestandCode)?.bezeichnung ?? f.tatbestandCode,
        faelligThisYear: 0, durchgefuehrt: 0, offen: 0,
      });
    }
    const s = map.get(f.tatbestandCode)!;
    s.faelligThisYear += 1;
    if (f.status === 'ERLEDIGT') { s.durchgefuehrt += 1; }
    else { s.offen += 1; }
  }
  return Array.from(map.values()).sort((a, b) => b.faelligThisYear - a.faelligThisYear);
}

function aggregateRechnungen(rechnungen: Rechnung[]): RechnungsStat {
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  return {
    offen:   rechnungen.filter(r => r.status === 'gestellt').length,
    bezahlt: rechnungen.filter(r => r.status === 'bezahlt').length,
    gemahnt: rechnungen.filter(r => r.status === 'gemahnt').length,
    umsatz:  rechnungen
      .filter(r => r.status === 'bezahlt' && r.datum >= monthStart)
      .reduce((s, r) => s + r.summeBrutto, 0),
  };
}

// ── KPI-Karte ─────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color }: {
  label: string; value: string | number; sub?: string; color?: string;
}): React.JSX.Element {
  return (
    <div className="card">
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <p className={`text-3xl font-bold mt-1 ${color ?? 'text-gray-900 dark:text-gray-100'}`}>{value}</p>
      {sub !== undefined && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

// ── Mini-Balkendiagramm ──────────────────────────────────────────────────

function BarChart({ data }: {
  data: { label: string; value: number; max: number; color?: string }[];
}): React.JSX.Element {
  return (
    <div className="space-y-2">
      {data.map(d => (
        <div key={d.label} className="flex items-center gap-3">
          <span className="text-xs text-gray-500 w-20 flex-shrink-0 text-right">{d.label}</span>
          <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${d.color ?? 'bg-blue-500'}`}
              style={{ width: d.max > 0 ? `${Math.round((d.value / d.max) * 100)}%` : '0%' }} />
          </div>
          <span className="text-xs font-mono font-bold w-6 flex-shrink-0">{d.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Hauptkomponente ───────────────────────────────────────────────────────

type ReportingTab = 'vorsorge' | 'rechnungen' | 'jahresbericht';

export function ReportingModul(): React.JSX.Element {
  const mandant = useMandantStore(s => s.getAktiverMandant());
  const [tab,    setTab]    = useState<ReportingTab>('vorsorge');
  const [jahr,   setJahr]   = useState(String(new Date().getFullYear()));
  const [vguStats,    setVguStats]    = useState<VguStat[]>([]);
  const [rechnStats,  setRechnStats]  = useState<RechnungsStat>({ offen: 0, bezahlt: 0, gemahnt: 0, umsatz: 0 });
  const [gbuEintraege, setGbuEintraege] = useState<GefaehrdungsEintrag[]>([]);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    setLoading(true);
    void Promise.all([
      loadFaelligkeiten(mandant?.id),
      loadRechnungen(),
      loadSetting<GefaehrdungsEintrag[]>('gbu_eintraege_v1', []),
    ]).then(([faelligkeiten, rechnungen, gbu]) => {
      const rechnGefilt = mandant !== null && mandant !== undefined
        ? rechnungen.filter(r => String(r.mandantId) === mandant.id)
        : rechnungen;
      setVguStats(aggregateVgu(faelligkeiten));
      setRechnStats(aggregateRechnungen(rechnGefilt));
      setGbuEintraege(gbu);
      setLoading(false);
    });
  }, [mandant?.id]);

  const gesamtFaellig = vguStats.reduce((s, v) => s + v.faelligThisYear, 0);
  const gesamtDurch   = vguStats.reduce((s, v) => s + v.durchgefuehrt,   0);
  const gesamtOffen   = vguStats.reduce((s, v) => s + v.offen,            0);
  const quote         = gesamtFaellig > 0 ? Math.round((gesamtDurch / gesamtFaellig) * 100) : 0;

  const handlePrint = (): void => { window.print(); };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 pt-4 flex gap-1">
        {([
          { key: 'vorsorge'      as ReportingTab, label: 'Vorsorge',      icon: '🩺' },
          { key: 'rechnungen'    as ReportingTab, label: 'Umsatz',         icon: '💶' },
          { key: 'jahresbericht' as ReportingTab, label: 'Jahresbericht',  icon: '📊' },
        ]).map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors
              ${tab === t.key
                ? 'border-blue-600 text-blue-700 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}>
            <span>{t.icon}</span><span>{t.label}</span>
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">GF-Reporting</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {mandant !== null && mandant !== undefined
                ? <span style={{ color: mandant.farbe }}>{mandant.name}</span>
                : 'Alle Mandanten'
              } · {jahr}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select value={jahr} onChange={e => { setJahr(e.target.value); }}
              className="input-field text-sm max-w-28">
              {['2024','2025','2026'].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={handlePrint} className="btn-secondary text-sm">🖨 Drucken</button>
          </div>
        </div>

        {loading && <div className="text-center py-12 text-gray-400">Lade Daten…</div>}

        {/* ── Vorsorge ── */}
        {!loading && tab === 'vorsorge' && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <KpiCard label="Fällig gesamt" value={gesamtFaellig} sub={jahr} />
              <KpiCard label="Durchgeführt"  value={gesamtDurch}
                color="text-green-600 dark:text-green-400" sub="erledigt" />
              <KpiCard label="Offen"         value={gesamtOffen}
                color={gesamtOffen > 0 ? 'text-orange-600' : 'text-gray-900 dark:text-gray-100'}
                sub="ausstehend" />
              <KpiCard label="Quote"         value={`${quote} %`}
                color={quote >= 90 ? 'text-green-600 dark:text-green-400' : 'text-orange-600'}
                sub="Vollständigkeit" />
            </div>

            {vguStats.length === 0 ? (
              <div className="card text-center py-10 text-gray-400">
                <p className="text-3xl mb-2">🩺</p>
                <p>Noch keine VGÜ-Fälligkeiten eingetragen</p>
                <p className="text-sm mt-1">Im Modul „Vorsorge → Fälligkeiten" eintragen</p>
              </div>
            ) : (
              <div className="card overflow-hidden p-0">
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
                  <h2 className="font-semibold text-sm text-gray-900 dark:text-gray-100">Vorsorgen nach Tatbestand</h2>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      {['Code','Bezeichnung','Fällig','Durchg.','Offen','Quote'].map(h => (
                        <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-2">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {vguStats.map(v => {
                      const q = v.faelligThisYear > 0
                        ? Math.round((v.durchgefuehrt / v.faelligThisYear) * 100) : 100;
                      return (
                        <tr key={v.tatbestandCode}>
                          <td className="px-4 py-2.5 font-mono text-xs font-bold text-blue-600 dark:text-blue-400">{v.tatbestandCode}</td>
                          <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100">{v.bezeichnung}</td>
                          <td className="px-4 py-2.5 text-right text-gray-600">{v.faelligThisYear}</td>
                          <td className="px-4 py-2.5 text-right text-green-600 font-medium">{v.durchgefuehrt}</td>
                          <td className={`px-4 py-2.5 text-right font-medium ${v.offen > 0 ? 'text-orange-600' : 'text-gray-400'}`}>{v.offen}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-2">
                                <div className={`h-full rounded-full ${q >= 100 ? 'bg-green-500' : q >= 80 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                  style={{ width: `${Math.min(q, 100)}%` }} />
                              </div>
                              <span className="text-xs font-mono w-8">{q}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* GBU-Zusammenfassung */}
            {(() => {
              const gbuHoch   = gbuEintraege.filter(g => g.eintrittsWs * g.schwere >= 12).length;
              const gbuOffen  = gbuEintraege.filter(g => !g.erledigt).length;
              const gbuGesamt = gbuEintraege.length;
              return gbuGesamt > 0 ? (
                <div className="card space-y-3">
                  <h2 className="font-semibold text-sm text-gray-900 dark:text-gray-100">Gefährdungsbeurteilung (GBU)</h2>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Gefährdungen gesamt</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-0.5">{gbuGesamt}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Hohes Risiko (≥ 12)</p>
                      <p className={`text-2xl font-bold mt-0.5 ${gbuHoch > 0 ? 'text-red-600' : 'text-gray-400'}`}>{gbuHoch}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Maßnahmen offen</p>
                      <p className={`text-2xl font-bold mt-0.5 ${gbuOffen > 0 ? 'text-orange-600' : 'text-green-600'}`}>{gbuOffen}</p>
                    </div>
                  </div>
                  {gbuHoch > 0 && (
                    <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                      ⚠ {gbuHoch} Gefährdung{gbuHoch > 1 ? 'en' : ''} mit hohem Risiko — sofortige Maßnahmen erforderlich
                    </p>
                  )}
                </div>
              ) : null;
            })()}
          </div>
        )}

        {/* ── Rechnungen / Umsatz ── */}
        {!loading && tab === 'rechnungen' && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <KpiCard label="Offen"   value={rechnStats.offen}
                color={rechnStats.offen > 0 ? 'text-yellow-600' : 'text-gray-900 dark:text-gray-100'} />
              <KpiCard label="Gemahnt" value={rechnStats.gemahnt}
                color={rechnStats.gemahnt > 0 ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'} />
              <KpiCard label="Bezahlt" value={rechnStats.bezahlt}
                color="text-green-600 dark:text-green-400" />
              <KpiCard label="Umsatz lfd. Monat"
                value={`${(rechnStats.umsatz / 100).toLocaleString('de-AT', { minimumFractionDigits: 2 })} €`}
                color="text-blue-700 dark:text-blue-400" />
            </div>

            {rechnStats.offen === 0 && rechnStats.bezahlt === 0 ? (
              <div className="card text-center py-10 text-gray-400">
                <p className="text-3xl mb-2">💶</p>
                <p>Noch keine Rechnungen gespeichert</p>
                <p className="text-sm mt-1">Im Modul „Abrechnung → Neue Rechnung" erstellen</p>
              </div>
            ) : (
              <div className="card">
                <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 text-sm">Rechnungsstatus</h2>
                <BarChart data={[
                  { label: 'Bezahlt', value: rechnStats.bezahlt, max: rechnStats.bezahlt + rechnStats.offen + rechnStats.gemahnt, color: 'bg-green-500' },
                  { label: 'Offen',   value: rechnStats.offen,   max: rechnStats.bezahlt + rechnStats.offen + rechnStats.gemahnt, color: 'bg-yellow-400' },
                  { label: 'Gemahnt', value: rechnStats.gemahnt, max: rechnStats.bezahlt + rechnStats.offen + rechnStats.gemahnt, color: 'bg-red-400' },
                ]} />
              </div>
            )}
          </div>
        )}

        {/* ── Jahresbericht ── */}
        {!loading && tab === 'jahresbericht' && (
          <div className="card prose prose-sm dark:prose-invert max-w-none">
            <h2 className="text-base font-bold mb-4">Jahresbericht Arbeitsmedizin {jahr}</h2>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <strong>Auftraggeber:</strong>{' '}
              {mandant !== null && mandant !== undefined ? mandant.name : 'Alle Mandanten'}
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
              <strong>Erstellt von:</strong> Dr. med. Klaus Karrer, MSc. — Arbeitsmediziner
            </p>

            <div className="mt-4 space-y-3 text-sm text-gray-700 dark:text-gray-300">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">1. Vorsorgeuntersuchungen</h3>
              {gesamtFaellig > 0 ? (
                <p>
                  Im Jahr {jahr} wurden <strong>{gesamtDurch}</strong> von{' '}
                  <strong>{gesamtFaellig}</strong> fälligen Vorsorgeuntersuchungen durchgeführt
                  (Quote: <strong>{quote}%</strong>).
                  {gesamtOffen > 0 && ` Es verbleiben ${gesamtOffen} offene Untersuchungen.`}
                </p>
              ) : (
                <p>Noch keine Daten eingetragen.</p>
              )}

              <h3 className="font-semibold text-gray-900 dark:text-gray-100">2. Rechnungen / Umsatz</h3>
              <p>
                {rechnStats.bezahlt > 0
                  ? `${rechnStats.bezahlt} Rechnung(en) bezahlt, ${rechnStats.offen} offen, ${rechnStats.gemahnt} gemahnt.`
                  : 'Noch keine Rechnungen erfasst.'
                }
              </p>

              <h3 className="font-semibold text-gray-900 dark:text-gray-100">3. Gefährdungsbeurteilung</h3>
              <p>
                {gbuEintraege.length > 0
                  ? `${gbuEintraege.length} Gefährdungen erfasst, davon ${gbuEintraege.filter(g => g.eintrittsWs * g.schwere >= 12).length} mit hohem Risiko. ${gbuEintraege.filter(g => !g.erledigt).length} Maßnahmen noch offen.`
                  : 'Noch keine GBU-Einträge im Modul ASA/GBU erfasst.'
                }
              </p>

              <h3 className="font-semibold text-gray-900 dark:text-gray-100">4. Empfehlungen</h3>
              <p>
                Auf Basis der Auswertungen empfehle ich die zeitnahe Nachholung der offenen
                Vorsorgeuntersuchungen sowie die Überprüfung der Gefährdungsbeurteilungen für
                Arbeitsplätze mit erhöhtem Risikopotential.
              </p>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-800 flex justify-end">
              <button onClick={handlePrint} className="btn-primary">🖨 Bericht drucken / PDF</button>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-400 text-center">
          ✅ Live-Daten aus Dexie v5 · Vorsorge + Rechnungen
        </p>
      </div>
    </div>
  );
}
